import { useMemo } from "react";
import { useAuthUser } from "react-auth-kit";
import { Badge, NumberInput, Textarea, TextInput } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { IconPencil, IconTrash } from "@tabler/icons";
import ActionCellRender from "../../components/actionCellRender";
import FileList from "../../components/fileList";
import ExpenseClaimWizard from "../../components/expense/expenseClaimWizard";
import {
  ExpenseAmountHkdField,
  ExpenseClaimStatus,
  ExpenseClaimSummary,
  ExpenseCurrencySelect,
  ExpenseItemsField,
  ExpenseReceiptDropzone,
  ExpenseTypeSelect,
  DepartmentSelect,
  ReceiptPreview,
} from "../../components/expense/expenseClaimFields";
import { approvalStatusLabel } from "../../components/expense/expenseClaimFields";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
  PageList,
} from "../../components/pageList";

const apiEntity = "expenseClaim";

const ExpenseClaimPage = () => {
  const auth = useAuthUser();
  const user = auth?.() ?? {};

  const formSchema = useMemo(
    () => {
      const shared = {
        title: "Expense Claim",
        isMainForm: true,
        display: {
          mode: ENUM_FORM_DISPLAY.LINK,
          size: "xl",
        },
        closeAfterSave: false,
        apiEntity,
        initialValues: {
          claimantName: user.name || "",
          department: user.departmentId || null,
          currency: "HKD",
          items: [],
          preApprovalFiles: [],
          receiptFile: null,
        },
      };
      return {
        ...shared,
        add: {
          ...shared,
          display: {
            mode: ENUM_FORM_DISPLAY.MODAL,
            size: 1100,
          },
          showSaveBar: false,
          layout: {
            containers: [
              {
                key: "wizard-box",
                parent: null,
                type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
                props: {},
              },
            ],
            fields: [
              {
                name: "wizard",
                component: ExpenseClaimWizard,
                parent: "wizard-box",
                props: {},
              },
            ],
          },
        },
        edit: {
          ...shared,
          layout: {
        containers: [
          {
            key: "edit-columns",
            parent: null,
            type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
            props: { gutter: "xl", columns: 24, align: "flex-start" },
            cols: [
              {
                key: "edit-status",
                props: {
                  span: 5,
                  style: { alignSelf: "flex-start", position: "sticky", top: 16 },
                },
              },
              {
                key: "edit-main",
                props: { span: 19 },
              },
            ],
          },
          {
            key: "section-status",
            parent: "edit-status",
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: "Status",
              description: "Where this claim is in approval",
              mt: 0,
              paperPadding: "md",
              style: { minHeight: 0 },
            },
          },
          {
            key: "section-claim",
            parent: "edit-main",
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: "Claim",
              description: "Who is claiming, and which expense type applies",
              mt: 0,
              style: { minHeight: 0 },
            },
          },
          {
            key: "claim-fields",
            parent: "section-claim",
            type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
            props: {},
          },
          {
            key: "section-receipt-file",
            parent: "edit-main",
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: "Receipt file",
              description: "The receipt, then the upload",
              style: { minHeight: 0 },
            },
          },
          {
            key: "receipt-file-fields",
            parent: "section-receipt-file",
            type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
            props: {},
          },
          {
            key: "section-receipt-figures",
            parent: "edit-main",
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: "Receipt figures",
              description: "What was printed on the receipt",
              style: { minHeight: 0 },
            },
          },
          {
            key: "receipt-figure-fields",
            parent: "section-receipt-figures",
            type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
            props: {},
          },
          {
            key: "section-receipt-hkd",
            parent: "edit-main",
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: "Hong Kong amount",
              description: "Approval uses this amount, not the foreign total",
              style: { minHeight: 0 },
            },
          },
          {
            key: "receipt-hkd-fields",
            parent: "section-receipt-hkd",
            type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
            props: {},
          },
          {
            key: "section-receipt-lines",
            parent: "edit-main",
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: "Receipt lines",
              description: "Each item printed on the receipt",
              style: { minHeight: 0 },
            },
          },
          {
            key: "section-preapproval",
            parent: "edit-main",
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: "Pre-approval",
              description: "Attach the email that approved this expense",
              style: { minHeight: 0 },
            },
          },
          {
            key: "section-notes",
            parent: "edit-main",
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: "Notes",
              style: { minHeight: 0 },
            },
          },
          {
            key: "section-approval",
            parent: "edit-main",
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: "Approval",
              description: "Who has to approve this claim before it can be paid",
              style: { minHeight: 0 },
            },
          },
        ],
        fields: [
          {
            name: "approvalStatus",
            component: ExpenseClaimStatus,
            parent: "section-status",
            props: {},
          },
          {
            name: "claimantName",
            component: TextInput,
            parent: "claim-fields",
            props: { label: "Claimant", disabled: true },
          },
          {
            name: "department",
            component: DepartmentSelect,
            parent: "claim-fields",
            props: { label: "Department" },
          },
          {
            name: "expenseType",
            component: ExpenseTypeSelect,
            parent: "claim-fields",
            props: { label: "Expense type", required: true },
          },
          {
            name: "purpose",
            component: Textarea,
            parent: "claim-fields",
            props: { label: "Purpose", minRows: 2 },
          },
          {
            name: "receiptPreview",
            component: ReceiptPreview,
            parent: "receipt-file-fields",
            props: {},
          },
          {
            name: "receiptFile",
            component: ExpenseReceiptDropzone,
            parent: "receipt-file-fields",
            props: { label: "Receipt" },
          },
          {
            name: "merchant",
            component: TextInput,
            parent: "receipt-figure-fields",
            props: { label: "Merchant" },
          },
          {
            name: "receiptDate",
            component: DatePicker,
            parent: "receipt-figure-fields",
            props: { label: "Receipt date" },
          },
          {
            name: "currency",
            component: ExpenseCurrencySelect,
            parent: "receipt-figure-fields",
            props: {},
          },
          {
            name: "total",
            component: NumberInput,
            parent: "receipt-figure-fields",
            props: { label: "Total", precision: 2, hideControls: true },
          },
          {
            name: "subtotal",
            component: NumberInput,
            parent: "receipt-figure-fields",
            props: { label: "Subtotal", precision: 2, hideControls: true },
          },
          {
            name: "tax",
            component: NumberInput,
            parent: "receipt-figure-fields",
            props: { label: "Tax", precision: 2, hideControls: true },
          },
          {
            name: "amountHkd",
            component: ExpenseAmountHkdField,
            parent: "receipt-hkd-fields",
            props: {},
          },
          {
            name: "attendeeCount",
            component: NumberInput,
            parent: "receipt-hkd-fields",
            visibleOnly: (values) =>
              values.expenseTypeMode === "PER_ATTENDEE" ||
              values.expenseType?.thresholdMode === "PER_ATTENDEE",
            props: {
              label: "Attendees",
              description: "Whole number of people at the entertainment",
              min: 1,
              step: 1,
              precision: 0,
            },
          },
          {
            name: "items",
            component: ExpenseItemsField,
            parent: "section-receipt-lines",
            props: {},
          },
          {
            name: "preApprovalFiles",
            component: FileList,
            parent: "section-preapproval",
            props: { label: "Pre-approval email" },
          },
          {
            name: "remark",
            component: Textarea,
            parent: "section-notes",
            props: { label: "Remark", minRows: 2 },
          },
          {
            name: "summary",
            component: ExpenseClaimSummary,
            parent: "section-approval",
            props: {},
          },
        ],
      },
        },
      };
    },
    [user.department, user.name]
  );

  const tableSchema = useMemo(
    () => ({
      showActionColumn: true,
      searchableFields: ["code", "merchant", "claimantName", "purpose", "departmentName"],
      columns: [
        { field: "code", headerName: "Code" },
        { field: "claimantName", headerName: "Claimant" },
        {
          field: "expenseType",
          headerName: "Expense type",
          cellRender: (col, data) => data.expenseType?.name || "-",
        },
        {
          field: "department",
          headerName: "Department",
          cellRender: (col, data) => data.department?.name || data.departmentName || "-",
        },
        { field: "merchant", headerName: "Merchant" },
        {
          field: "total",
          headerName: "Total",
          cellRender: (col, data) =>
            data.total == null ? "-" : `${data.currency || ""} ${data.total}`,
        },
        {
          field: "status",
          headerName: "Status",
          cellRender: (col, data) => {
            const requestStatus =
              data.mcaRequestId && typeof data.mcaRequestId === "object"
                ? data.mcaRequestId.status
                : data.mcaPending
                  ? "PENDING"
                  : "";
            const badge = approvalStatusLabel(data.status, requestStatus);
            return (
              <Badge color={badge.color} variant="light">
                {badge.label}
              </Badge>
            );
          },
        },
        {
          field: "action",
          cellRender: ActionCellRender,
          isAction: true,
          actions: [{ name: "edit", icon: IconPencil, props: {} }],
          actionMenu: [
            {
              name: "delete",
              label: "Delete data",
              icon: IconTrash,
              props: { color: "red" },
            },
          ],
        },
      ],
    }),
    []
  );

  return (
    <PageList
      title="Expense Claims"
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
    />
  );
};

export default ExpenseClaimPage;
